import { PIN_STATUSES } from "./propertyPins";

/**
 * Map-pin markers for job tracker pins: a teardrop in the status colour with a
 * white centre and outline, its tip on the property, over a soft ground shadow.
 *
 * Drawn on a canvas (not loaded from a file) so the images can be added to the
 * map synchronously, including from Mapbox's `styleimagemissing` event after a
 * basemap switch wipes them.
 */

const PREFIX = "rr-pin-";
const UNKNOWN = "unknown";
const UNKNOWN_COLOR = "#6B7280";

/** Icon size in CSS pixels; drawn at 2x so it stays crisp on retina screens. */
export const PIN_ICON_WIDTH = 28;
export const PIN_ICON_HEIGHT = 38;
export const PIN_ICON_PIXEL_RATIO = 2;
/** The tip sits this far above the image's bottom edge (room for the shadow). */
export const PIN_ICON_TIP_INSET = 4;
/** From the tip to the top of the pin, for placing popups above it. */
export const PIN_ICON_TIP_TO_TOP = PIN_ICON_HEIGHT - PIN_ICON_TIP_INSET;

export function pinIconId(statusKey) {
  return `${PREFIX}${statusKey}`;
}

export function isPinIconId(id) {
  return typeof id === "string" && id.startsWith(PREFIX);
}

const ICONS = [
  ...PIN_STATUSES.map((status) => ({ id: pinIconId(status.key), color: status.color })),
  { id: pinIconId(UNKNOWN), color: UNKNOWN_COLOR },
];

/** Mapbox expression picking each pin's marker by its status. */
export const PIN_ICON_EXPRESSION = [
  "match",
  ["get", "status"],
  ...PIN_STATUSES.flatMap((status) => [status.key, pinIconId(status.key)]),
  pinIconId(UNKNOWN),
];

/** One marker as ImageData, or null where canvas is unavailable (e.g. jsdom). */
export function drawPinIcon(color, pixelRatio = PIN_ICON_PIXEL_RATIO) {
  const canvas = document.createElement("canvas");
  canvas.width = PIN_ICON_WIDTH * pixelRatio;
  canvas.height = PIN_ICON_HEIGHT * pixelRatio;
  let ctx = null;
  try {
    ctx = canvas.getContext("2d");
  } catch (error) {
    ctx = null;
  }
  if (!ctx) return null;

  ctx.scale(pixelRatio, pixelRatio);
  const cx = PIN_ICON_WIDTH / 2;
  const tipY = PIN_ICON_HEIGHT - PIN_ICON_TIP_INSET;
  const r = 10;
  const cy = r + 2;

  // Ground shadow under the tip.
  ctx.fillStyle = "rgba(0, 0, 0, 0.28)";
  ctx.beginPath();
  ctx.ellipse(cx, tipY, 5, 2, 0, 0, Math.PI * 2);
  ctx.fill();

  // Teardrop: two tangents from the tip to the round head.
  const reach = Math.acos(r / (tipY - cy));
  ctx.beginPath();
  ctx.moveTo(cx, tipY);
  ctx.arc(cx, cy, r, Math.PI / 2 - reach, Math.PI / 2 + reach, true);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineJoin = "round";
  ctx.lineWidth = 2;
  ctx.strokeStyle = "#fff";
  ctx.stroke();

  // White centre.
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, Math.PI * 2);
  ctx.fillStyle = "#fff";
  ctx.fill();

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** Add every status's marker the map doesn't have yet. */
export function addPinIcons(map) {
  ICONS.forEach(({ id, color }) => {
    if (map.hasImage(id)) return;
    const image = drawPinIcon(color);
    if (image) map.addImage(id, image, { pixelRatio: PIN_ICON_PIXEL_RATIO });
  });
}

/**
 * Add the markers now and again whenever the map asks for one it lost, as it
 * does after the satellite/streets toggle replaces the style.
 */
export function installPinIcons(map) {
  addPinIcons(map);
  map.on("styleimagemissing", (event) => {
    if (isPinIconId(event.id)) addPinIcons(map);
  });
}
